import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ElementInfo, ElementType, ElementWithUserNames, ElementDataStatus } from '../../types/element.types';
import { getCurrentUserInfo } from '../../utils/userUtils';
import { sortElements, sortCastItemsByNO } from '../../utils/elementSortUtils';
import { useElementDuplicateCheck } from './useElementsDuplicateCheck';
import { getAllElementsInBreakdownCache } from './getAllElementsInBreakdownCache';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import { useElementUpdates, type UseElementUpdatesOptions } from '../../hooks/dataUpdates/useElementUpdates';
// 导入 SVG 图标组件
import { ReactComponent as UncheckedIcon } from '../../assets/svg/Breakdown/unchecked.svg';
import { ReactComponent as CheckedIcon } from '../../assets/svg/Breakdown/checked.svg';
import { ReactComponent as ViewIcon } from '../../assets/svg/Breakdown/view.svg';
import './ElementSearch.css';

interface ElementSearchProps {
  value: string | null | undefined;
  onChange?: (element: ElementInfo, isCreated?: boolean) => void; // 单选回调，isCreated标识是否为新创建的元素
  visible: boolean;
  onClose: () => void;
  breakdownID: string;  // 分场ID，查询属于该分场的所有场次元素
  currentElementType: ElementType;
  searchText?: string; // 外部传入的搜索关键词
  sceneID?: string; // 当前场次ID，用于创建关系
  selectedElementIds?: string[]; // 已选择的场次元素ID列表
  maxColumns?: number; // 最大列数限制，默认为3
  fixedColumns?: number; // 固定列数，设置后忽略自动计算，始终使用指定列数
  widthAutoFit?: boolean; // 宽度自适应，默认为false（固定240px每列）
  columnOrderLeftToRight?: boolean; // 列顺序从左到右，默认为false（从右到左）
  useViewIcon?: boolean; // 使用 view 图标替代 unchecked/checked 图标，默认为false
  hideCreateButton?: boolean; // 隐藏"新建"按钮，默认为false
  /** 是否允许直接编辑元素序号、名称和简称；关闭后仍可点击选择元素 */
  editable?: boolean;
  /** 隐藏简称/数量搜索框（元素管理页不需要数量搜索） */
  hideShortNameQuantityInput?: boolean;
  /** 受限日期与计划日期重叠的 elementID 集合，用于元素管理页显示冲突日期色 */
  elementIdsWithRestrictedDatesOverlap?: Set<string>;
}

const ElementSearch: React.FC<ElementSearchProps> = ({ 
  value, 
  onChange, 
  visible, 
  onClose, 
  breakdownID,
  currentElementType,
  searchText = '',
  sceneID,
  selectedElementIds = [],
  maxColumns = 3,
  fixedColumns,
  widthAutoFit = false,
  columnOrderLeftToRight = false,
  useViewIcon = false,
  hideCreateButton = false,
  editable = true,
  hideShortNameQuantityInput = false,
  elementIdsWithRestrictedDatesOverlap,
}) => {
  const { t } = useTranslation();
  const [searchResults, setSearchResults] = useState<ElementWithUserNames[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputText, setInputText] = useState('');
  const [shortNameInputText, setShortNameInputText] = useState(''); // 简称/数量搜索输入框
  const searchRef = useRef<HTMLDivElement>(null);
  
  // 场次元素序号编辑状态
  const [isEditingElementNO, setIsEditingElementNO] = useState(false);
  const [editingElementNOItemId, setEditingElementNOItemId] = useState<string | null>(null); // 当前正在编辑序号的元素ID
  const [elementNOValue, setElementNOValue] = useState('');
  const elementNOInputRef = useRef<HTMLInputElement>(null);
  
  // 元素名称编辑状态
  const [isEditingElementName, setIsEditingElementName] = useState(false);
  const [editingElementNameItemId, setEditingElementNameItemId] = useState<string | null>(null);
  const [elementNameValue, setElementNameValue] = useState('');
  const elementNameInputRef = useRef<HTMLInputElement>(null);
  
  // 简称/数量编辑状态
  const [isEditingShortName, setIsEditingShortName] = useState(false);
  const [editingShortNameItemId, setEditingShortNameItemId] = useState<string | null>(null);
  const [shortNameValue, setShortNameValue] = useState('');
  const shortNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editable) return;
    setIsEditingElementNO(false);
    setEditingElementNOItemId(null);
    setIsEditingElementName(false);
    setEditingElementNameItemId(null);
    setIsEditingShortName(false);
    setEditingShortNameItemId(null);
    currentEditingInputRef.current = null;
  }, [editable]);
  
  // 创建加载状态
  const [createLoading, setCreateLoading] = useState(false);
  
  // Tab 键导航相关状态
  const nameInputRef = useRef<HTMLInputElement>(null);
  const shortNameInputRef2 = useRef<HTMLInputElement>(null);
  const createButtonRef = useRef<HTMLButtonElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const [focusedElement, setFocusedElement] = useState<string | null>(null);
  
  // 缓存管理
  const { getElementCache, subscribeElementCache } = useDataCache();
  const { createElementData, updateElementData } = useElementUpdates({
    onSuccess: (action, data) => {
      // 数据更新成功，重新加载搜索结果（缓存已由 hook 更新）
      if (visible) {
        loadDefaultResults();
      }
    },
    onError: (error, action, data) => {
      console.error(`[ElementSearch] ${action} 失败:`, error);
    },
  } satisfies UseElementUpdatesOptions);
  
  // 使用 ref 存储最新的编辑值，避免在 useEffect 依赖中导致循环
  const editValuesRef = useRef<{[key: string]: string}>({});
  const currentEditingInputRef = useRef<HTMLInputElement | null>(null);
  
  // 确定当前正在编辑的元素和字段（用于重名检查 hook）
  const editingItem = (isEditingElementName && editingElementNameItemId) 
    ? editingElementNameItemId 
    : (isEditingShortName && editingShortNameItemId && currentElementType === 'CAST')
      ? editingShortNameItemId
      : null;
  const editingField = (isEditingElementName && editingElementNameItemId) 
    ? 'name' 
    : (isEditingShortName && editingShortNameItemId && currentElementType === 'CAST')
      ? 'description'
      : null;
  
  // 使用重名检查 hook
  const {
    hasDuplicate: hasDuplicateAndEditing,
    triggerCheck,
  } = useElementDuplicateCheck({
    editingItem,
    editingField,
    editValuesRef,
    elements: searchResults,
    breakdownID,
    currentElementType,
    onDuplicateStateChange: (hasDuplicate) => {
      // 可以在这里处理重名状态变化
    },
    currentEditingInputRef,
  });
  
  // 是否有重复的状态（用于按钮样式，创建时）
  const [hasDuplicate, setHasDuplicate] = useState(false);
  // 分别跟踪元素名称和简称的重名状态
  const [hasDuplicateName, setHasDuplicateName] = useState(false);
  const [hasDuplicateDescription, setHasDuplicateDescription] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  /** 用于在切换场次元素类型时清空搜索框，且避免与「显示时加载」重复跑两次 */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- ref 在下方 effect 中通过 .current 读写，部分 ESLint 版本未识别
  const prevElementTypeRef = useRef<ElementType | null>(null);
  const [layoutConfig, setLayoutConfig] = useState({
    width: 480, // 改为和2列一样的宽度
    columns: 1,
    maxHeight: typeof window !== 'undefined' ? window.innerHeight - 44 : 800,
    rowsPerColumn: 0,
    totalRows: 0
  });

  /** 弹层距视口顶部的实际像素 + 底边留白，用于 maxHeight；与 Breakdown(≈40px)、EditScene(--edit-scene-drawer-top+3) 等 CSS 对齐 */
  const getViewportTopReserve = useCallback(() => {
    const bottomMargin = 4;
    if (typeof window === 'undefined') return 44 + bottomMargin;
    const el = searchRef.current;
    if (el) {
      const top = el.getBoundingClientRect().top;
      if (Number.isFinite(top)) {
        return Math.max(0, top) + bottomMargin;
      }
    }
    return 44 + bottomMargin;
  }, []);

  const getScrollbarWidth = useCallback(() => {
    if (typeof document === 'undefined') return 0;

    const scrollDiv = document.createElement('div');
    scrollDiv.style.width = '100px';
    scrollDiv.style.height = '100px';
    scrollDiv.style.overflow = 'scroll';
    scrollDiv.style.position = 'absolute';
    scrollDiv.style.top = '-9999px';
    document.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);

    return Math.max(0, scrollbarWidth);
  }, []);

  // 计算桌面端固定布局下的搜索结果区布局（不依赖 containerRef：高度按视口，避免首帧 ref 未就绪导致 maxHeight=0）
  const calculateLayout = useCallback((resultCount: number) => {
    const topReserve = getViewportTopReserve();
    const maxHeight =
      typeof window !== 'undefined' ? window.innerHeight - topReserve : 800;

    const itemHeight = 24;
    const gap = 0;
    const inputAreaHeight = 40;
    const availableHeight = maxHeight - inputAreaHeight - 2;
    const maxRowsPerColumn = Math.max(1, Math.floor(availableHeight / (itemHeight + gap)));

    // 先按结果数量确定列数，再用「当前列数」算每列行数与是否溢出（原先固定除以 3，1/2 列时会误判不滚动并截断）
    let columns = 1;
    let width = 240;
    const actualMaxColumns = Math.min(maxColumns, 3);

    if (fixedColumns !== undefined && fixedColumns >= 1 && fixedColumns <= 3) {
      columns = fixedColumns;
      width = fixedColumns * 240;
    } else if (resultCount <= maxRowsPerColumn) {
      columns = 1;
      width = actualMaxColumns >= 2 ? 480 : 240;
    } else if (resultCount <= 2 * maxRowsPerColumn && actualMaxColumns >= 2) {
      columns = 2;
      width = 480;
    } else if (actualMaxColumns >= 3) {
      columns = 3;
      width = 720;
    } else if (actualMaxColumns >= 2) {
      columns = 2;
      width = 480;
    }

    const rowsNeeded = resultCount === 0 ? 0 : Math.ceil(resultCount / columns);
    const needsScroll = rowsNeeded > maxRowsPerColumn;
    const totalRows = needsScroll ? Math.max(1, rowsNeeded) : maxRowsPerColumn;
    const isWindowsBrowser =
      typeof navigator !== 'undefined' && /win/i.test(navigator.userAgent);
    const scrollbarCompensation =
      isWindowsBrowser && needsScroll && !widthAutoFit ? getScrollbarWidth() : 0;

    setLayoutConfig({
      width: width + scrollbarCompensation,
      columns,
      maxHeight: Math.max(120, maxHeight),
      rowsPerColumn: maxRowsPerColumn,
      totalRows,
    });
  }, [maxColumns, fixedColumns, widthAutoFit, getScrollbarWidth, getViewportTopReserve]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      // 延迟计算，确保filteredResults已定义
      setTimeout(() => {
        calculateLayout(searchResults.length);
      }, 0);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [searchResults.length, calculateLayout]);

  // 加载默认结果（按规则排序：是否常用、elementSceneNumber）
  // 仅从 BreakdownDataCacheContext 的 element 缓存读取；分场页写入缓存前不打接口，由 subscribe 在缓存就绪后再刷新
  const loadDefaultResults = useCallback(async () => {
    setLoading(true);
    try {
      const cache = getElementCache(breakdownID);
      if (cache === null) {
        setSearchResults([]);
        return;
      }
      const data = cache.elements.filter(item => item.elementType === currentElementType);
      if (data.length > 0) {
        const sortedResults = currentElementType === ElementType.CAST
          ? sortCastItemsByNO(data)
          : sortElements(data);
        setSearchResults(sortedResults);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('ElementSearch加载默认结果失败:', error);
      setSearchResults([]);
    } finally {
      if (getElementCache(breakdownID) !== null) {
        setLoading(false);
      }
    }
  }, [breakdownID, currentElementType, getElementCache]);


  // 处理搜索（与默认列表一致，仅从缓存过滤，不请求 element 列表接口）
  const handleSearch = useCallback(async (nameText: string, shortNameText: string = '') => {
    if (!nameText.trim() && !shortNameText.trim()) {
      // 没有搜索关键词时，显示默认结果
      loadDefaultResults();
      return;
    }

    setLoading(true);
    try {
      const cache = getElementCache(breakdownID);
      if (cache === null) {
        setSearchResults([]);
        return;
      }
      let data = cache.elements.filter(item => item.elementType === currentElementType);

      // 前端过滤：按名称和简称/数量过滤
      const filteredResults = data.filter(item => {
        const nameMatch = !nameText.trim() || 
          (item.elementName && item.elementName.toLowerCase().includes(nameText.toLowerCase()));
        
        let shortNameMatch = true;
        if (shortNameText.trim()) {
          if (currentElementType === ElementType.CAST) {
            // 主要角色：搜索简称
            shortNameMatch = !!(item.elementAbbr && 
              item.elementAbbr.toLowerCase().includes(shortNameText.toLowerCase()));
          } else if (currentElementType === ElementType.BACKGROUND || 
                     currentElementType === ElementType.FEATURED ||
                     currentElementType === ElementType.STUNT_PLAYERS ||
                     currentElementType === ElementType.ANIMALS ||
                     currentElementType === ElementType.VEHICLES) {
            // 群众演员、特约演员、动作演员、动物、车辆：搜索数量（使用 elementMaxQtyInBreakdown）
            shortNameMatch = !!(item.elementMaxQtyInBreakdown && 
              item.elementMaxQtyInBreakdown.toString().includes(shortNameText));
          }
        }
        
        return nameMatch && shortNameMatch;
      });
      data = filteredResults;
      
      // 如果是主要角色，使用特殊排序规则：优先按 elementNO 排序
      const sortedResults = currentElementType === ElementType.CAST 
        ? sortCastItemsByNO(data)
        : sortElements(data);
      
      setSearchResults(sortedResults);
    } catch (error) {
      console.error('ElementSearch搜索失败:', error);
      setSearchResults([]);
    } finally {
      if (getElementCache(breakdownID) !== null) {
        setLoading(false);
      }
    }
  }, [breakdownID, currentElementType, loadDefaultResults, getElementCache]);

  const handleShortNameInputFocus = useCallback(() => {
    // 只在主要角色分类下，才自动同步第一个字符到简称
    if (currentElementType !== ElementType.CAST) {
      return;
    }
    
    // 如果简称为空且元素名称有内容，才同步第一个字符
    setShortNameInputText(prev => {
      // 如果简称为空，才自动同步
      if (!prev.trim()) {
        const trimmedName = inputText.trim();
        if (trimmedName) {
          const firstChar = trimmedName.charAt(0);
          if (firstChar) {
            return firstChar;
          }
        }
      }
      return prev;
    });
  }, [inputText, currentElementType]);

  // 处理从输入框创建元素
  const handleCreateFromInputs = async () => {
    if (!inputText.trim()) {
      alert('请输入元素名称');
      return;
    }

    // 主要角色类型下，简称是必填字段
    if (currentElementType === 'CAST' && !shortNameInputText.trim()) {
      alert('主要角色类型下，简称是必填字段');
      return;
    }

    // 检查重名
    const isDuplicate = await checkDuplicateName(inputText.trim(), shortNameInputText.trim() || undefined);
    if (isDuplicate) {
      // 不弹出系统对话框，重名提示区域会显示
      return;
    }

    setCreateLoading(true);
    const shouldRefocusToName = focusedElement === 'create'; // 记录是否从新增按钮触发
    try {
      const userInfo = getCurrentUserInfo();
      if (!userInfo) {
        alert('用户信息获取失败');
        return;
      }

      // 如果是主要角色，需要计算并设置 elementNO
      let elementNO: string | undefined = undefined;
      if (currentElementType === ElementType.CAST) {
        try {
          const cache = getElementCache(breakdownID);
          const allCastItems: ElementWithUserNames[] =
            cache?.elements?.filter(item => item.elementType === ElementType.CAST) ?? [];

          // 计算最大序号
          let maxNO = 0;
          allCastItems.forEach(item => {
            if (item.elementNO && item.elementNO.trim() !== '') {
              const no = parseInt(item.elementNO, 10);
              if (!isNaN(no) && no > maxNO) {
                maxNO = no;
              }
            }
          });
          
          // 新序号 = 最大序号 + 1
          elementNO = (maxNO + 1).toString();
        } catch (error) {
          console.error('获取主要角色场次元素列表失败，使用默认序号:', error);
          // 如果获取失败，使用默认值 1
          elementNO = '1';
        }
      }

      // 对于数量字段（特约角色、群众演员、动作演员、动物、车辆），如果数量为空或无效，默认为1
      let elementQtyInScene: number | undefined = undefined;
      if (currentElementType === ElementType.BACKGROUND || 
          currentElementType === ElementType.FEATURED ||
          currentElementType === ElementType.STUNT_PLAYERS ||
          currentElementType === ElementType.ANIMALS ||
          currentElementType === ElementType.VEHICLES) {
        const parsedQuantity = parseInt(shortNameInputText.trim());
        elementQtyInScene = isNaN(parsedQuantity) || parsedQuantity <= 0 ? 1 : parsedQuantity;
      }

      // 使用统一的创建 hook
      const newElementId = await createElementData(breakdownID, {
        elementName: inputText.trim(),
        elementAbbr: currentElementType === ElementType.CAST ? (shortNameInputText.trim() || undefined) : undefined,
        elementBelongBreakdownID: breakdownID,
        elementType: currentElementType,
        elementSceneNumber: 1,
        elementNO: elementNO,
        elementCreatorID: userInfo.userID,
        elementDataStatus: ElementDataStatus.NORMAL,
        sceneID: sceneID,  // 传入 sceneID 用于创建关系
        elementQtyInScene: elementQtyInScene  // 传入数量用于创建关系
      });

      if (newElementId) {
        console.log('成功创建场次元素:', newElementId);
        
        // 从缓存中获取新创建的元素数据
        const cache = getElementCache(breakdownID);
        const newElement = cache?.elements?.find(item => item.elementID === newElementId);
        
        if (newElement) {
          // 创建：选择新创建的元素但不关闭组件
          onChange?.(newElement, true);
        }
        
        // 清除输入内容
        setInputText('');
        setShortNameInputText('');
        
        // 重新加载默认结果，显示新创建的元素
        loadDefaultResults();
        
        // 如果是从新增按钮触发的，重新 focus 到元素名称输入框
        if (shouldRefocusToName) {
          setTimeout(() => {
            nameInputRef.current?.focus();
            setFocusedElement('name');
          }, 100);
        }
      } else {
        console.error('创建场次元素返回数据不完整');
        alert('创建场次元素成功但数据不完整');
      }
    } catch (error) {
      console.error('创建场次元素失败:', error);
      alert(t('elementSearch.createFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    } finally {
      setCreateLoading(false);
    }
  };

  // 订阅缓存更新，当数据变化时自动刷新搜索结果
  useEffect(() => {
    if (!visible || !breakdownID) return;
    
    const unsubscribe = subscribeElementCache(breakdownID, () => {
      // 缓存更新时，重新加载搜索结果
      if (visible) {
        loadDefaultResults();
      }
    });
    
    return unsubscribe;
  }, [visible, breakdownID, subscribeElementCache, loadDefaultResults]);

  // 显示/搜索文案/元素类型变化时加载列表（合并 effect，避免与「仅类型变化」重复请求）
  useEffect(() => {
    if (!visible) return;
    if (prevElementTypeRef.current !== null && prevElementTypeRef.current !== currentElementType) {
      setInputText('');
      setShortNameInputText('');
    }
    prevElementTypeRef.current = currentElementType;
    if (searchText) {
      handleSearch(searchText, shortNameInputText);
    } else {
      loadDefaultResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, searchText, currentElementType]);

  // 处理点击外部关闭搜索框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  const runSelectionChangeCallbacks = async (
    element: ElementWithUserNames,
    isCreated?: boolean
  ) => {
    await Promise.resolve(onChange?.(element, isCreated));
  };

  const handleSelect = async (element: ElementWithUserNames) => {
    // 如果元素已选择，则不允许再次选择
    if (selectedElementIds.includes(element.elementID)) {
      return;
    }
    await runSelectionChangeCallbacks(element, false);
    // 不清空搜索结果和输入框，保持搜索状态以便继续选择
    // 不调用 onClose()，让用户可以通过点击外部区域或关闭按钮手动关闭
  };

  // 检查元素名称和简称是否重复（用于创建时）
  const checkDuplicateName = useCallback(async (elementName: string, elementAbbreviation?: string): Promise<boolean> => {
    // 如果既没有元素名称也没有简称，或者没有breakdownID，则不检查
    if ((!elementName.trim() && !elementAbbreviation?.trim()) || !breakdownID) {
      setHasDuplicate(false);
      setHasDuplicateName(false);
      setHasDuplicateDescription(false);
      return false;
    }
    
    try {
      // 分别检查元素名称和简称是否重名
      let checkName = false;
      let checkDescription = false;
      
      // 检查元素名称是否重名（只检查名称字段）
      if (elementName.trim()) {
        // 调用 hook 内部的 checkDuplicate，只检查名称字段
        const allElements = await getAllElementsInBreakdownCache(breakdownID, getElementCache);
        checkName = allElements.some((item: ElementWithUserNames) => 
          item.elementName?.trim() === elementName.trim() && 
          item.elementType === currentElementType
        );
      }
      
      // 检查简称是否重名（仅主要角色类型，只检查简称字段）
      if (elementAbbreviation?.trim() && currentElementType === 'CAST') {
        const allElements = await getAllElementsInBreakdownCache(breakdownID, getElementCache);
        checkDescription = allElements.some((item: ElementWithUserNames) => 
          item.elementAbbr?.trim() === elementAbbreviation.trim() && 
          item.elementType === currentElementType
        );
      }
      
      setHasDuplicateName(checkName);
      setHasDuplicateDescription(checkDescription);
      
      const hasDuplicateResult = checkName || checkDescription;
      setHasDuplicate(hasDuplicateResult);
      return hasDuplicateResult;
    } catch (error) {
      console.error('重名检查失败:', error);
      setHasDuplicate(false);
      setHasDuplicateName(false);
      setHasDuplicateDescription(false);
      return false;
    }
  }, [breakdownID, currentElementType, getElementCache]);

  // 实时检查重复（当输入内容变化时）
  useEffect(() => {
    // 防抖：延迟500ms后检查
    const timer = setTimeout(() => {
      // 如果元素名称或简称任一有输入，就检查重复
      // 这样可以检查：1) 只输入元素名称的情况 2) 只输入简称的情况 3) 两者都输入的情况
      if (inputText.trim() || shortNameInputText.trim()) {
        checkDuplicateName(inputText.trim(), shortNameInputText.trim() || undefined);
      } else {
        // 如果元素名称和简称都没有输入，清除重复状态
        setHasDuplicate(false);
        setHasDuplicateName(false);
        setHasDuplicateDescription(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [inputText, shortNameInputText, checkDuplicateName]);

  // 辅助函数：执行重名检查（用于输入值变化时）
  const performDuplicateCheckOnChange = useCallback(() => {
    triggerCheck();
  }, [triggerCheck]);

  // 全局点击事件监听：当有重名时阻止所有点击（除了当前输入框和添加按钮）
  useEffect(() => {
    if (!hasDuplicateAndEditing) return;

    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // 允许点击的元素：
      // 1. 任意编辑中的 input（支持多列时同一项有多个 input 实例，或 ref 未正确指向时仍可正常选择文本、插入光标）
      // 2. 添加按钮
      const isEditingInput = target.closest('input.element-search-input-field') !== null;
      const isCurrentInput = isEditingInput || (currentEditingInputRef.current && (
        currentEditingInputRef.current === target ||
        currentEditingInputRef.current.contains(target)
      ));
      
      const isAddButton = target.closest('.element-searchresults-add-button') !== null;
      
      // 如果点击的不是允许的元素，阻止事件
      if (!isCurrentInput && !isAddButton) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // 重新聚焦到输入框
        if (currentEditingInputRef.current) {
          setTimeout(() => {
            currentEditingInputRef.current?.focus();
          }, 0);
        }
      }
    };

    // 使用捕获阶段来确保在所有其他事件处理器之前执行
    document.addEventListener('click', handleGlobalClick, true);
    document.addEventListener('mousedown', handleGlobalClick, true);
    
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
      document.removeEventListener('mousedown', handleGlobalClick, true);
    };
  }, [hasDuplicateAndEditing]);

  // 不再需要获取场次信息，因为序号现在属于场次元素本身

  /**
   * 计算序号冲突时需要更新的序号列表
   * @param newNO 新的序号（字符串，可能是数字）
   * @param allElements 所有场次元素列表（同分场、同类型）
   * @param currentElementId 当前正在修改的场次元素ID
   * @param originalNO 当前元素的原序号
   * @returns 需要更新的序号映射 { elementID: newNO }
   */
  const calculateNOConflicts = (
    newNO: string,
    allElements: ElementWithUserNames[],
    currentElementId: string,
    originalNO: string
  ): Map<string, string> => {
    const updates = new Map<string, string>();
    
    // 如果新序号为空，直接返回
    if (!newNO || !newNO.trim()) {
      return updates;
    }

    // 尝试将新序号转换为数字
    const newNONum = parseInt(newNO.trim(), 10);
    if (isNaN(newNONum)) {
      // 如果不是数字，不处理冲突
      return updates;
    }

    // 获取所有其他元素的序号（排除当前元素）
    const otherItems = allElements.filter(item => item.elementID !== currentElementId);
    const existingNOs = new Set<number>();
    const itemNOMap = new Map<number, string>(); // number -> elementID

    otherItems.forEach(item => {
      if (item.elementNO) {
        const noNum = parseInt(item.elementNO.trim(), 10);
        if (!isNaN(noNum)) {
          existingNOs.add(noNum);
          itemNOMap.set(noNum, item.elementID);
        }
      }
    });

    // 如果新序号不存在冲突，直接返回
    if (!existingNOs.has(newNONum)) {
      return updates;
    }

    // 找到从新序号开始的连续序号段
    let currentNO = newNONum;
    const affectedNOs: number[] = [];

    // 收集所有需要后移的连续序号
    while (existingNOs.has(currentNO)) {
      affectedNOs.push(currentNO);
      currentNO++;
    }

    // 如果没有需要后移的序号，直接返回
    if (affectedNOs.length === 0) {
      return updates;
    }

    // 从后往前处理，避免中间序号冲突
    // 计算每个序号应该变成的新值
    const noUpdates = new Map<number, number>(); // oldNO -> newNO
    const usedNewValues = new Set<number>(); // 记录已经使用的新值
    
    for (let i = affectedNOs.length - 1; i >= 0; i--) {
      const oldNO = affectedNOs[i];
      let newValue = oldNO + 1;
      
      // 检查新值是否已被使用，或者是否与未受影响的现有序号冲突
      while (usedNewValues.has(newValue) || (existingNOs.has(newValue) && !affectedNOs.includes(newValue))) {
        newValue++;
      }
      
      noUpdates.set(oldNO, newValue);
      usedNewValues.add(newValue);
    }

    // 构建更新映射
    noUpdates.forEach((newValue, oldNO) => {
      const elementID = itemNOMap.get(oldNO);
      if (elementID) {
        updates.set(elementID, newValue.toString());
      }
    });

    return updates;
  };

  // 处理序号输入框失焦事件
  const handleElementNOBlur = async (elementId: string, originalValue: string) => {
    if (elementId && elementNOValue !== originalValue) {
      try {
        // 获取所有同分场、同类型的场次元素（优先使用缓存）
        const allElements = await getAllElementsInBreakdownCache(breakdownID, getElementCache);
        const filteredItems = allElements.filter(
          (item: ElementWithUserNames) => item.elementType === currentElementType
        );

        // 计算需要更新的序号冲突
        const conflictUpdates = calculateNOConflicts(
          elementNOValue,
          filteredItems,
          elementId,
          originalValue
        );

        // 批量更新所有受影响的场次元素
        const updatePromises: Promise<void>[] = [];
        
        // 更新当前元素
        updatePromises.push(updateElementData(breakdownID, elementId, { elementNO: elementNOValue }));
        
        // 更新冲突的元素
        conflictUpdates.forEach((newNO, itemId) => {
          updatePromises.push(updateElementData(breakdownID, itemId, { elementNO: newNO }));
        });

        // 等待所有更新完成
        await Promise.all(updatePromises);

        // 更新搜索结果中的值并重新排序
        setSearchResults(prev => {
          const updated = prev.map(item => {
            if (item.elementID === elementId) {
              return { ...item, elementNO: elementNOValue };
            }
            // 更新冲突的元素
            const conflictNO = conflictUpdates.get(item.elementID);
            if (conflictNO !== undefined) {
              return { ...item, elementNO: conflictNO };
            }
            return item;
          });
          // 如果是主要角色，使用特殊排序规则：优先按 elementNO 排序
          if (currentElementType === ElementType.CAST) {
            return sortCastItemsByNO(updated);
          } else {
            return sortElements(updated);
          }
        });
      } catch (error) {
        console.error('更新场次元素序号失败:', error);
        // 恢复原值
        setElementNOValue(originalValue);
      }
    }
    setIsEditingElementNO(false);
    setEditingElementNOItemId(null);
  };

  // 处理序号输入框回车事件
  const handleElementNOKeyDown = (e: React.KeyboardEvent, elementId: string, originalValue: string) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Escape') {
      setElementNOValue(originalValue);
      setIsEditingElementNO(false);
      setEditingElementNOItemId(null);
    }
  };

  // 处理元素名称输入框失焦事件
  const handleElementNameBlur = async (e: React.FocusEvent, elementId: string, originalValue: string) => {
    // 如果有重名，阻止失焦
    if (hasDuplicateAndEditing) {
      e.preventDefault();
      e.stopPropagation();
      // 重新聚焦到输入框
      setTimeout(() => {
        const input = e.target as HTMLInputElement;
        input.focus();
        currentEditingInputRef.current = input;
      }, 0);
      return;
    }
    
    if (elementId && elementNameValue !== originalValue && elementNameValue.trim()) {
      try {
        await updateElementData(breakdownID, elementId, { elementName: elementNameValue.trim() });
        
        // 更新搜索结果中的值
        setSearchResults(prev => {
          const updated = prev.map(item => {
            if (item.elementID === elementId) {
              return { ...item, elementName: elementNameValue.trim() };
            }
            return item;
          });
          // 保持排序
          if (currentElementType === ElementType.CAST) {
            return sortCastItemsByNO(updated);
          } else {
            return sortElements(updated);
          }
        });
      } catch (error) {
        console.error('更新元素名称失败:', error);
        // 恢复原值
        setElementNameValue(originalValue);
      }
    }
      setIsEditingElementName(false);
      setEditingElementNameItemId(null);
      setElementNameValue('');
      currentEditingInputRef.current = null;
      editValuesRef.current = {};
  };

  // 处理元素名称输入框回车事件
  const handleElementNameKeyDown = (e: React.KeyboardEvent, elementId: string, originalValue: string) => {
    if (e.key === 'Enter') {
      // 如果有重名，阻止回车键退出编辑状态
      if (hasDuplicateAndEditing) {
        e.preventDefault();
        return;
      }
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Escape') {
      setElementNameValue(originalValue);
      setIsEditingElementName(false);
      setEditingElementNameItemId(null);
      currentEditingInputRef.current = null;
      editValuesRef.current = {};
    }
  };

  // 处理简称/数量输入框失焦事件
  const handleShortNameBlur = async (e: React.FocusEvent, elementId: string, originalValue: string) => {
    // 如果是主要角色的简称字段且有重名，阻止失焦
    if (currentElementType === 'CAST' && hasDuplicateAndEditing) {
      e.preventDefault();
      e.stopPropagation();
      // 重新聚焦到输入框
      setTimeout(() => {
        const input = e.target as HTMLInputElement;
        input.focus();
        currentEditingInputRef.current = input;
      }, 0);
      return;
    }
    
    if (elementId && shortNameValue !== originalValue) {
      try {
        // 根据类型决定更新哪个字段
        const updateData: any = {};
        if (currentElementType === 'CAST') {
          updateData.elementAbbr = shortNameValue.trim();
        } else if (currentElementType === 'BACKGROUND' || 
                   currentElementType === 'FEATURED' ||
                   currentElementType === 'STUNT_PLAYERS' ||
                   currentElementType === 'ANIMALS' ||
                   currentElementType === 'VEHICLES') {
          // 转换为数字，如果无效则使用默认值1
          const quantity = parseInt(shortNameValue.trim(), 10);
          updateData.elementQuantity = !isNaN(quantity) && quantity > 0 ? quantity : 1;
        }
        
        await updateElementData(breakdownID, elementId, updateData);
        
        // 更新搜索结果中的值
        setSearchResults(prev => {
          const updated = prev.map(item => {
            if (item.elementID === elementId) {
              if (currentElementType === 'CAST') {
                return { ...item, elementAbbr: shortNameValue.trim() };
              } else {
                const quantity = parseInt(shortNameValue.trim(), 10);
                return { ...item, elementQuantity: !isNaN(quantity) && quantity > 0 ? quantity : 1 };
              }
            }
            return item;
          });
          // 保持排序
          if (currentElementType === ElementType.CAST) {
            return sortCastItemsByNO(updated);
          } else {
            return sortElements(updated);
          }
        });
      } catch (error) {
        console.error('更新简称/数量失败:', error);
        // 恢复原值
        setShortNameValue(originalValue);
      }
    }
      setIsEditingShortName(false);
      setEditingShortNameItemId(null);
      setShortNameValue('');
      currentEditingInputRef.current = null;
      editValuesRef.current = {};
  };

  // 处理简称/数量输入框回车事件
  const handleShortNameKeyDown = (e: React.KeyboardEvent, elementId: string, originalValue: string) => {
    if (e.key === 'Enter') {
      // 如果是主要角色的简称字段且有重名，阻止回车键退出编辑状态
      if (currentElementType === 'CAST' && hasDuplicateAndEditing) {
        e.preventDefault();
        return;
      }
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Escape') {
      setShortNameValue(originalValue);
      setIsEditingShortName(false);
      setEditingShortNameItemId(null);
      currentEditingInputRef.current = null;
      editValuesRef.current = {};
    }
  };

  // 处理搜索框关闭时的清理
  useEffect(() => {
    if (!visible) {
      setSearchResults([]);
      setInputText('');
      setShortNameInputText('');
      setHasDuplicate(false);
      setHasDuplicateName(false);
      setHasDuplicateDescription(false);
      setIsEditingElementNO(false);
      setEditingElementNOItemId(null);
      setElementNOValue('');
      setIsEditingElementName(false);
      setEditingElementNameItemId(null);
      setElementNameValue('');
      setIsEditingShortName(false);
      setEditingShortNameItemId(null);
      setShortNameValue('');
      currentEditingInputRef.current = null;
      editValuesRef.current = {};
      setFocusedElement(null);
    } else {
      // 组件打开时，自动 focus 到元素名称输入框
      setTimeout(() => {
        nameInputRef.current?.focus();
        setFocusedElement('name');
      }, 100);
    }
  }, [visible]);
  
  // 处理 Tab 键导航（仅拦截全局统一导航，保留浏览器原生 Tab 顺序）
  useEffect(() => {
    if (!visible) return;

    const handleTabNavigation = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      
      // 检查是否在 ElementSearch 组件内
      const target = e.target as HTMLElement;
      const searchRoot = target.closest('.element-search');
      if (!searchRoot) return;
      
      // 仅阻止全局统一导航接管；Tab 顺序由浏览器原生处理
      e.stopPropagation();
      e.stopImmediatePropagation();

      const tabOrder: HTMLElement[] = [];
      if (nameInputRef.current && !nameInputRef.current.disabled) {
        tabOrder.push(nameInputRef.current);
      }
      if (shortNameInputRef2.current && !shortNameInputRef2.current.disabled && shortNameInputRef2.current.offsetParent !== null) {
        tabOrder.push(shortNameInputRef2.current);
      }
      if (createButtonRef.current && !createButtonRef.current.disabled && createButtonRef.current.offsetParent !== null) {
        tabOrder.push(createButtonRef.current);
      }
      if (searchButtonRef.current && !searchButtonRef.current.disabled) {
        tabOrder.push(searchButtonRef.current);
      }

      if (tabOrder.length === 0) return;

      const activeElement = document.activeElement as HTMLElement | null;
      const firstElement = tabOrder[0];
      const lastElement = tabOrder[tabOrder.length - 1];

      // 边界回环：最后一个按 Tab 回到第一个；第一个按 Shift+Tab 回到最后一个
      if (!e.shiftKey && activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
        return;
      }
      if (e.shiftKey && activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
        return;
      }
    };
    
    // 使用捕获阶段，确保最优先处理（在 useTabKeyHandler 之前）
    // 添加到 window 而不是 document，优先级更高
    window.addEventListener('keydown', handleTabNavigation, true);
    
    return () => {
      window.removeEventListener('keydown', handleTabNavigation, true);
    };
  }, [visible]);
  
  // 处理方向键事件（仅在 ElementSearch 组件内有效，禁用全局方向键导航）
  useEffect(() => {
    if (!visible) return;
    
    const handleArrowKeyNavigation = (e: KeyboardEvent) => {
      // 只处理方向键
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        return;
      }
      
      // 检查是否在 ElementSearch 组件内（包括输入框和搜索结果区域）
      const target = e.target as HTMLElement;
      const isInElementSearch = target.closest('.element-search') !== null;
      
      if (isInElementSearch) {
        // 如果焦点在输入框内（正在编辑），允许方向键移动光标，不阻止事件
        const isInputElement = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
        if (isInputElement) {
          // 输入框处于编辑状态，允许方向键移动光标，不阻止事件
          // 检查输入框是否处于 focus 状态（document.activeElement）
          if (document.activeElement === target) {
            // 输入框正在编辑，允许方向键移动光标
            return;
          }
        }
        
        // 在 ElementSearch 内但不在输入框编辑状态，阻止事件传播，禁用全局方向键导航
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    
    // 使用捕获阶段，确保最优先处理（在 useArrowKeyHandler 之前）
    // 添加到 window 而不是 document，优先级更高
    window.addEventListener('keydown', handleArrowKeyNavigation, true);
    
    return () => {
      window.removeEventListener('keydown', handleArrowKeyNavigation, true);
    };
  }, [visible]);
  
  // 处理新增按钮的回车键
  const handleCreateButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateFromInputs();
    }
  };

  // 计算按钮状态
  const getButtonState = () => {
    // 优先检查是否有重复（只要有重复就显示重复状态，不需要等必填内容都填写）
    if (hasDuplicate) {
      return 'duplicate';
    }
    
    // 检查必填内容是否已填写
    const isRequiredFilled = inputText.trim() && 
      (currentElementType !== 'CAST' || shortNameInputText.trim());
    
    // 如果必填内容未填写
    if (!isRequiredFilled) {
      return 'incomplete';
    }
    
    // 通过检查且必填内容都填写了
    return 'ready';
  };

  const buttonState = getButtonState();

  // 过滤结果（按全部）
  const filteredResults = searchResults;

  // 将搜索结果按列优先分组（第1列从右往左数排满，再排第2列）
  const groupResultsIntoColumns = (items: ElementWithUserNames[]) => {
    const actualColumns = layoutConfig.columns;
    const columns: ElementWithUserNames[][] = Array(actualColumns).fill(null).map(() => []);
    
    // 按列优先排列：先填满第1列，再填第2列，最后填第3列
    items.forEach((item, index) => {
      const columnIndex = Math.floor(index / layoutConfig.totalRows);
      if (columnIndex < actualColumns) {
        columns[columnIndex].push(item);
      }
    });
    
    return columns;
  };

  // 计算布局配置
  // 使用 useLayoutEffect 确保在 DOM 更新后同步计算，避免闪烁
  useLayoutEffect(() => {
    if (visible) {
      calculateLayout(filteredResults.length);
    }
  }, [filteredResults.length, visible, calculateLayout]);

  // 获取当前布局配置
  const columns = groupResultsIntoColumns(filteredResults);
  const isWindows =
    typeof navigator !== 'undefined' && /win/i.test(navigator.userAgent);

  if (!visible) return null;

  return (
    <div 
      className="element-search" 
      ref={searchRef}
      style={{
        '--element-search-width': `${layoutConfig.width}px`
      } as React.CSSProperties}
    >
      <div 
        className="element-search-dialog"
        ref={containerRef}
        aria-label={String(t(`sceneDetails.itemTabs.${currentElementType}`))}
        style={{
          width: widthAutoFit ? '100%' : `${layoutConfig.width}px`,
          maxHeight: `${layoutConfig.maxHeight}px`
        }}
      >
        {/* 搜索输入框 */}
        <div className="element-search-input-container">
          <div className="element-search-input-row">
            <input
              ref={nameInputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !loading) {
                  handleSearch(inputText, shortNameInputText);
                }
              }}
              onFocus={() => setFocusedElement('name')}
              placeholder={t('elementSearch.searchPlaceholder')}
              className={`element-search-input ${hasDuplicateName ? 'duplicate-error' : ''} ${focusedElement === 'name' ? 'focused' : ''}`}
            />
            {/* 简称/数量搜索输入框 - 主要角色显示简称，群众演员、特约演员等显示数量；元素管理页通过 hideShortNameQuantityInput 隐藏 */}
            {!hideShortNameQuantityInput &&
              (currentElementType === 'CAST' || 
              currentElementType === 'BACKGROUND' ||
              currentElementType === 'FEATURED' ||
              currentElementType === 'STUNT_PLAYERS' ||
              currentElementType === 'ANIMALS' ||
              currentElementType === 'VEHICLES') && (
              <input
                ref={shortNameInputRef2}
                type="text"
                value={shortNameInputText}
                onChange={(e) => setShortNameInputText(e.target.value)}
                onFocus={() => {
                  handleShortNameInputFocus();
                  setFocusedElement('shortName');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !loading) {
                    handleSearch(inputText, shortNameInputText);
                  }
                }}
                placeholder={currentElementType === 'CAST' ? t('elementSearch.shortNamePlaceholder') : t('elementSearch.quantityPlaceholder')}
                className={`element-search-input element-search-input-shortname ${hasDuplicateDescription ? 'duplicate-error' : ''} ${focusedElement === 'shortName' ? 'focused' : ''}`}
              />
            )}
            {/* 新增按钮（hideCreateButton 为 true 时隐藏） */}
            {!hideCreateButton && (
              <button
                ref={createButtonRef}
                className={`element-search-create-button element-search-create-button-${buttonState} ${focusedElement === 'create' ? 'focused' : ''}`}
                onClick={handleCreateFromInputs}
                onKeyDown={handleCreateButtonKeyDown}
                onFocus={() => setFocusedElement('create')}
                disabled={createLoading || buttonState !== 'ready'}
              >
                {createLoading ? t('elementSearch.create') : buttonState === 'duplicate' ? t('elementSearch.duplicate') : t('elementSearch.add')}
              </button>
            )}
            {/* 搜索按钮 */}
            <button
              ref={searchButtonRef}
              className={`element-search-search-button ${focusedElement === 'search' ? 'focused' : ''}`}
              onClick={() => handleSearch(inputText, shortNameInputText)}
              onFocus={() => setFocusedElement('search')}
              disabled={loading}
            >
              {t('elementSearch.search')}
            </button>
          </div>
        </div>
        

        {/* 内容区域 */}
        <div className="element-search-results">
          {/* 搜索结果列表 */}
          <>
            {loading ? (
              <div className="element-search-loading">{t('common.loading')}</div>
            ) : filteredResults.length > 0 ? (
              <div className={`element-search-results-container ${isWindows ? 'element-search-results-container-windows' : ''}`}>
                <div 
                  className={`element-search-columns ${columnOrderLeftToRight ? 'element-search-columns-left-to-right' : ''} ${widthAutoFit ? 'element-search-columns-auto-fit' : ''}`}
                >
                  {columns.map((column, columnIndex) => (
                    <div 
                      key={columnIndex} 
                      className={`element-search-column ${widthAutoFit ? 'element-search-column-auto-fit' : ''}`}
                    >
                      {column.map(item => {
                        const isSelected = selectedElementIds.includes(item.elementID);
                        return (
                        <div 
                          key={item.elementID}
                          className={`element-search-result-item ${isSelected ? 'element-searchresult-selected' : ''}`}
                          onClick={(e) => {
                            // 如果点击的是输入框或添加按钮，不触发选择
                            const target = e.target as HTMLElement;
                            if (target.closest('.element-search-input-field') || 
                                target.closest('.element-searchresults-add-button') ||
                                target.closest('.element-search-no-display') ||
                                target.closest('.element-search-add-icon')) {
                              return;
                            }
                            void handleSelect(item);
                          }}
                        >
                          <div className="element-search-item-layout">
                            {/* 左侧：序号、元素名称 */}
                            <div className="element-searchresults-left-section">
                              {/* 主要角色类型下，在元素名称前显示序号 */}
                              {currentElementType === 'CAST' && (
                                <>
                                  {editable && isEditingElementNO && editingElementNOItemId === item.elementID ? (
                                    <input
                                      ref={elementNOInputRef}
                                      type="text"
                                      value={elementNOValue}
                                      onChange={(e) => setElementNOValue(e.target.value)}
                                      onBlur={() => handleElementNOBlur(item.elementID, item.elementNO || '')}
                                      onKeyDown={(e) => handleElementNOKeyDown(e, item.elementID, item.elementNO || '')}
                                      className="element-search-no-input element-search-input-field"
                                      autoFocus
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    <div 
                                      className={`element-search-no-display${editable ? ' element-search-input-field' : ''}`}
                                      onClick={editable ? (e) => {
                                        e.stopPropagation();
                                        setIsEditingElementNO(true);
                                        setEditingElementNOItemId(item.elementID);
                                        setElementNOValue(item.elementNO || '');
                                        // 确保输入框获得焦点
                                        setTimeout(() => {
                                          elementNOInputRef.current?.focus();
                                        }, 0);
                                      } : undefined}
                                    >
                                      {item.elementNO || ''}
                                    </div>
                                  )}
                                </>
                              )}
                              
                              {/* 元素名称 */}
                              {editable && isEditingElementName && editingElementNameItemId === item.elementID ? (
                                <input
                                  ref={(el) => {
                                    (elementNameInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                                    if (el) {
                                      currentEditingInputRef.current = el;
                                    }
                                  }}
                                  type="text"
                                  value={elementNameValue}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    setElementNameValue(newValue);
                                    // 更新 ref
                                    editValuesRef.current = {
                                      ...editValuesRef.current,
                                      name: newValue
                                    };
                                    // 触发重名检查
                                    setTimeout(() => {
                                      performDuplicateCheckOnChange();
                                    }, 0);
                                  }}
                                  data-item-id={item.elementID}
                                  data-field="name"
                                  onBlur={(e) => handleElementNameBlur(e, item.elementID, item.elementName || '')}
                                  onKeyDown={(e) => handleElementNameKeyDown(e, item.elementID, item.elementName || '')}
                                  className={`element-searchresults-input-name element-search-input-field ${
                                    (isEditingElementName && editingElementNameItemId === item.elementID && hasDuplicateAndEditing) 
                                      ? 'duplicate-error' : ''
                                  }`}
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  style={{ width: 'auto', flex: 1 }}
                                />
                              ) : (
                                <div 
                                  className={`element-search-name-display${editable ? ' element-search-input-field' : ''}`}
                                  onClick={editable ? (e) => {
                                    e.stopPropagation();
                                    setIsEditingElementName(true);
                                    setEditingElementNameItemId(item.elementID);
                                    const initialValue = item.elementName || '';
                                    setElementNameValue(initialValue);
                                    // 设置 ref
                                    editValuesRef.current = {
                                      name: initialValue,
                                      description: item.elementAbbr || ''
                                    };
                                    // 确保输入框获得焦点
                                    setTimeout(() => {
                                      elementNameInputRef.current?.focus();
                                    }, 0);
                                  } : undefined}
                                  style={{ width: 'auto', flex: 1 }}
                                >
                                  {item.elementName}
                                </div>
                              )}
                            </div>
                            
                            {/* 右侧：简称/数量、添加按钮 */}
                            <div className="element-searchresults-right-section">
                              {/* 简称/数量 */}
                              {currentElementType === 'CAST' ? (
                                // 主要角色：简称可编辑
                                editable && isEditingShortName && editingShortNameItemId === item.elementID ? (
                                  <input
                                    ref={(el) => {
                                      (shortNameInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                                      if (el) {
                                        currentEditingInputRef.current = el;
                                      }
                                    }}
                                    type="text"
                                    value={shortNameValue}
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      setShortNameValue(newValue);
                                      // 更新 ref（主要角色的简称需要重名检查）
                                      editValuesRef.current = {
                                        ...editValuesRef.current,
                                        description: newValue
                                      };
                                    // 触发重名检查
                                    setTimeout(() => {
                                      performDuplicateCheckOnChange();
                                    }, 0);
                                    }}
                                    onBlur={(e) => handleShortNameBlur(e, item.elementID, item.elementAbbr || '')}
                                    onKeyDown={(e) => handleShortNameKeyDown(e, item.elementID, item.elementAbbr || '')}
                                    data-item-id={item.elementID}
                                    data-field="description"
                                    className={`element-searchresults-input-shortname element-search-input-field ${
                                      (isEditingShortName && editingShortNameItemId === item.elementID && hasDuplicateAndEditing) 
                                        ? 'duplicate-error' : ''
                                    }`}
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  />
                                ) : (
                                  <div 
                                    className={`element-searchresults-input-shortname${editable ? ' element-search-input-field' : ''}`}
                                    onClick={editable ? (e) => {
                                      e.stopPropagation();
                                      setIsEditingShortName(true);
                                      setEditingShortNameItemId(item.elementID);
                                      const initialValue = item.elementAbbr || '';
                                      setShortNameValue(initialValue);
                                      // 设置 ref（主要角色的简称需要重名检查）
                                      editValuesRef.current = {
                                        name: item.elementName || '',
                                        description: initialValue
                                      };
                                      // 确保输入框获得焦点
                                      setTimeout(() => {
                                        shortNameInputRef.current?.focus();
                                      }, 0);
                                    } : undefined}
                                  >
                                    {item.elementAbbr || '--'}
                                  </div>
                                )
                              ) : (currentElementType === 'BACKGROUND' ||
                                    currentElementType === 'FEATURED' ||
                                    currentElementType === 'STUNT_PLAYERS' ||
                                    currentElementType === 'ANIMALS' ||
                                    currentElementType === 'VEHICLES') ? (
                                // 特约角色、群众演员、动作演员、动物、车辆：数量只读显示 elementMaxQtyInBreakdown
                                <div 
                                  className="element-searchresults-input-shortname"
                                  style={{ cursor: 'default' }}
                                >
                                  {item.elementMaxQtyInBreakdown || 1}
                                </div>
                              ) : null}
                              
                              {/* 添加按钮 */}
                              <div 
                                className={`element-searchresults-add-button ${isSelected ? 'checked' : 'unchecked'} ${useViewIcon ? 'element-searchresults-add-button-view' : ''}${elementIdsWithRestrictedDatesOverlap?.has(item.elementID) ? ' element-searchresults-add-button-conflict-dates' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isSelected) {
                                    void handleSelect(item);
                                  } else if (useViewIcon) {
                                    // 元素管理页：已选中时再次点「查看」仍通知父级（刷新 userLastUsedElementID 等）
                                    void runSelectionChangeCallbacks(item, false);
                                  }
                                }}
                              >
                                {useViewIcon ? (
                                  <ViewIcon 
                                    className="element-search-add-icon element-search-view-icon"
                                    aria-label="View"
                                  />
                                ) : isSelected ? (
                                  <CheckedIcon 
                                    className="element-search-add-icon"
                                    aria-label="Add"
                                  />
                                ) : (
                                  <UncheckedIcon 
                                    className="element-search-add-icon"
                                    aria-label="Add"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="element-search-empty">
                {inputText ? '未找到相关元素' : '该分类还没有元素，你可以在上方直接输入创建'}
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default ElementSearch;
